import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { RoomsDataTable } from "@/components/rooms/rooms.data-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { roomsApi } from "@/services/rooms/rooms.api";
import { $fetch } from "@/lib/fetch";
import { type RoomFormDto } from "slde-api/dto-schemas";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { type RoomEntity } from "slde-api/db-schemas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { RoomForm } from "@/components/rooms/room.form";
import { useResources } from "@/providers/resources.provider";

export default function RoomsPage() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [roomToUpdate, setRoomToUpdate] = useState<RoomEntity | null>(null);

  const { rooms } = useResources();

  const { mutate: createRoom, isPending: isCreatingRoom } = useMutation({
    mutationFn: async (form: RoomFormDto) =>
      await $fetch(roomsApi.rooms.route(), {
        method: "POST",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Salę utworzono pomyślnie");
      setCreateDialogOpen(false);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy dodawaniu sali: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: updateRoom, isPending: isUpdatingRoom } = useMutation({
    mutationFn: async ({ id, ...form }: RoomEntity) =>
      await $fetch(roomsApi.room.route(id), {
        method: "PATCH",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Zaktualizowano salę");
      setRoomToUpdate(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił bład przy aktualizacji sali: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: deleteRoom, isPending: isDeletingRoom } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await $fetch(roomsApi.room.route(id), {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Sala została usunięta");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy usuwaniu sali: ${e.message}`);
      }
      console.error(e);
    },
  });

  return (
    <div>
      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-base font-medium">Zarządzanie salami</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => setCreateDialogOpen(true)}>
              Dodaj sale
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <RoomsDataTable
          data={rooms}
          actions={{
            onUpdateClick: (room) => setRoomToUpdate(room),
            onDeleteClick: (id) => deleteRoom({ id }),
          }}
        />
      </div>

      <Dialog
        open={createDialogOpen || !!roomToUpdate}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          setRoomToUpdate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {!!roomToUpdate ? "Edytuj salę" : "Dodaj salę"}
            </DialogTitle>
          </DialogHeader>

          <RoomForm
            onSubmit={(form) =>
              !!roomToUpdate
                ? updateRoom({ id: roomToUpdate.id, ...form })
                : createRoom(form)
            }
            defaultValues={roomToUpdate}
            disabled={isCreatingRoom || isUpdatingRoom || isDeletingRoom}
            loading={isCreatingRoom || isUpdatingRoom || isDeletingRoom}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
